import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Task, BpmClient } from "@amaster.ai/bpm-client";

export interface MyTasksPageProps {
  /** BPM client instance */
  bpmClient: BpmClient;
  /** Current user identifier */
  currentUser: string;
  /** Called when clicking approve button */
  onApprove?: (task: Task) => void;
  /** Custom render for task name cell */
  renderTaskName?: (task: Task) => React.ReactNode;
  /** Custom render for actions cell */
  renderActions?: (task: Task, defaultAction: React.ReactNode) => React.ReactNode;
  /** Called when task list is refreshed */
  onRefresh?: () => void;
  /** Additional class name */
  className?: string;
}

/**
 * My Tasks Page - displays current user's pending tasks
 *
 * This is a shared component that can be used across different applications.
 * Each application sees the same UI but fetches different data based on the currentUser.
 */
export function MyTasksPage({
  bpmClient,
  currentUser,
  onApprove,
  renderTaskName,
  renderActions,
  onRefresh,
  className = "",
}: MyTasksPageProps) {
  const { t } = useTranslation("bpm");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await bpmClient.getTasks({
        assignee: currentUser,
      });
      if (fetchError) {
        setError(fetchError.message);
      } else {
        setTasks(data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  }, [bpmClient, currentUser]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleRefresh = () => {
    fetchTasks();
    onRefresh?.();
  };

  const handleApprove = (task: Task) => {
    onApprove?.(task);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const defaultActionButton = (task: Task) => (
    <button type="button" onClick={() => handleApprove(task)} className="bpm-btn bpm-btn-outline">
      {t("taskList.approve", "Approve")}
    </button>
  );

  return (
    <div className={`bpm-tasks-page ${className}`}>
      <div className="bpm-tasks-header">
        <h1 className="bpm-tasks-title">{t("taskList.myTasks", "My Tasks")}</h1>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          className="bpm-btn bpm-btn-outline"
        >
          {loading ? t("taskList.loading", "Loading...") : t("taskList.refresh", "Refresh")}
        </button>
      </div>

      {error && <div className="bpm-error">{error}</div>}

      <table className="bpm-table">
        <thead>
          <tr>
            <th>{t("taskList.taskName", "Task Name")}</th>
            <th>{t("taskList.processInstance", "Process Instance")}</th>
            <th>{t("taskList.createdAt", "Created At")}</th>
            <th>{t("taskList.dueDate", "Due Date")}</th>
            <th>{t("taskList.actions", "Actions")}</th>
          </tr>
        </thead>
        <tbody>
          {tasks.length === 0 ? (
            <tr>
              <td colSpan={5} className="bpm-table-empty">
                {loading
                  ? t("taskList.loading", "Loading...")
                  : t("taskList.noTasks", "No pending tasks")}
              </td>
            </tr>
          ) : (
            tasks.map((task) => (
              <tr key={task.id}>
                <td>{renderTaskName ? renderTaskName(task) : task.name}</td>
                <td className="bpm-mono">{task.processInstanceId?.slice(0, 8)}...</td>
                <td>{formatDate(task.created)}</td>
                <td>{formatDate(task.due)}</td>
                <td>
                  {renderActions
                    ? renderActions(task, defaultActionButton(task))
                    : defaultActionButton(task)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export type { Task };
